import { Link } from "react-router-dom";
import type { ComponentType } from "react";
import BrandLockup from "@/components/common/BrandLockup";
import {
  XIcon,
  InstagramIcon,
  YouTubeIcon,
} from "@/components/common/icons";
import { env } from "@/lib/env";
import { useLocale } from "@/i18n/useLocale";
import type { Locale } from "@/i18n/locales";
import { chromeFor } from "@/content/chrome";
import { pathFor, type RouteId } from "@/routes/manifest";
import type { ChromeContent } from "@/content/types";

interface SocialLink {
  label: string;
  href: string;
  Icon: ComponentType<{ size?: number; className?: string }>;
}

// Social handles point at "#" until the live URLs are confirmed
// with the client. Update each `href` once finalised.
const SOCIAL: readonly SocialLink[] = [
  { label: "X", href: "#", Icon: XIcon },
  { label: "Instagram", href: "#", Icon: InstagramIcon },
  { label: "YouTube", href: "#", Icon: YouTubeIcon },
];

interface FooterLink {
  label: string;
  to: string;
  external?: boolean;
}

interface FooterSection {
  title: string;
  links: ReadonlyArray<FooterLink>;
}

// The in-site link labels are read from `chrome.nav.labels` by route id rather
// than restated here, so the footer and the header cannot drift into two
// spellings of the same destination — the grouping is structure, the words are
// content, and only the grouping belongs in this file.
function sectionsFor(chrome: ChromeContent, locale: Locale): ReadonlyArray<FooterSection> {
  return [
    {
      title: chrome.footer.sections.product,
      links: [
        { label: chrome.nav.labels["/how-it-works"], to: pathFor("/how-it-works" as RouteId, locale) },
        { label: chrome.nav.labels["/platforms"], to: pathFor("/platforms" as RouteId, locale) },
        { label: chrome.nav.labels["/tradable-assets"], to: pathFor("/tradable-assets" as RouteId, locale) },
      ],
    },
    {
      title: chrome.footer.sections.company,
      links: [{ label: chrome.nav.labels["/support"], to: pathFor("/support" as RouteId, locale) }],
    },
    {
      title: chrome.footer.sections.legal,
      links: [
        { label: chrome.legal.terms, to: env.legalTermsUrl, external: true },
        { label: chrome.legal.privacy, to: env.legalPrivacyUrl, external: true },
      ],
    },
  ];
}

export default function Footer(): JSX.Element {
  const locale = useLocale();
  const chrome = chromeFor(locale);
  const sections = sectionsFor(chrome, locale);

  return (
    <footer className="bg-bg-2 border-t border-border pt-16 pb-6">
      <div className="container-page">
        <div className="grid gap-12 footer-grid">
          <div>
            <BrandLockup />
            <p className="text-fg-muted mt-4 text-sm max-w-[340px]">
              {chrome.footer.tagline}
            </p>
            <div className="flex gap-2.5 mt-5" aria-label={chrome.footer.ariaSocial}>
              {SOCIAL.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-md border border-border inline-flex items-center justify-center text-fg-muted hover:text-fg hover:border-border-strong transition-colors"
                  aria-label={label}
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>
          {sections.map((s) => (
            <div key={s.title}>
              <h5 className="font-mono text-[11px] font-bold tracking-[0.14em] uppercase text-fg-dim mb-3.5">
                {s.title}
              </h5>
              {s.links.map((l) =>
                l.external ? (
                  <a
                    key={l.label}
                    href={l.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block py-1.5 text-[13.5px] text-fg-muted hover:text-fg transition-colors"
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link
                    key={l.label}
                    to={l.to}
                    className="block py-1.5 text-[13.5px] text-fg-muted hover:text-fg transition-colors"
                  >
                    {l.label}
                  </Link>
                ),
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-border flex justify-between items-start gap-6 flex-wrap">
          <div className="max-w-[760px] text-fg-muted text-[11.5px] leading-[1.6]">
            <p>
              <strong className="text-fg-muted">{chrome.footer.riskWarningLabel}</strong>{" "}
              {chrome.footer.riskWarningBody}
            </p>
          </div>
          <div className="font-mono text-[11px] text-fg-dim tracking-[0.14em] text-right shrink-0">
            © {new Date().getFullYear()} FLEX FUND
            <br />
            {chrome.footer.rightsReserved}
          </div>
        </div>
      </div>
    </footer>
  );
}
